/**
 * Change the sign-in email — Settings → Security, both web portals.
 *
 * The OWASP sequence, on Clerk's own client SDK (a session-bound step-up for
 * free; Clerk sends, rate-limits and expires the codes):
 *
 *   1. Prove it is you — every Clerk call runs through `useStepUp`: Clerk asks
 *      for the password, or sends a code to the CURRENT address when the
 *      account has none (Google / LinkedIn / email-code sign-up).
 *   2. Prove the new address — Clerk adds it unverified and sends a 6-digit
 *      code to it. Nothing about sign-in changes until the code is right.
 *   3. Switch — the verified address becomes the primary, and the old one is
 *      removed. Unless a sign-in method is tied to it (Apple, Google, …): that
 *      address is the connected account's own, so it stays, and signing in
 *      with that provider keeps working.
 *
 * What follows is the platform's (on Clerk's `user.updated`): the mirror, the
 * Stripe customer, the security notice to the old address, the audit entry.
 *
 * States are a discriminated union (rule 11): the UI renders exactly one.
 * Failures carry Clerk's error — the screen puts it into words
 * (`useAuthErrorMessage`) and reports what it does not recognise.
 */
import { useUser } from '@clerk/clerk-react';
import { classifyClerkError, SOCIAL_PROVIDER_NAME, socialProviderOf } from '@tonnie/foundation';
import { useCallback, useState } from 'react';

import { useStepUp } from './useStepUp';

export type ChangeEmailState =
  | { step: 'idle' }
  | { step: 'editing' }
  | { step: 'sending'; email: string }
  | { step: 'code_sent'; email: string }
  | { step: 'verifying'; email: string }
  /**
   * The new address is the sign-in email now. `linkedOld`: the old address
   * stayed because a connected account signs in through it (`provider` is its
   * brand name, `null` for one outside our list).
   */
  | { step: 'done'; email: string; linkedOld: { email: string; provider: string | null } | null }
  /** Switched — but the old address could not be removed (`error`: why, `null` if dismissed). */
  | { step: 'old_kept'; email: string; oldEmail: string; error: unknown }
  /** Clerk refused: the address belongs to a DIFFERENT account. */
  | { step: 'email_taken'; email: string }
  /**
   * `send`: the new address was not accepted (the form stays); `verify`: the
   * code was not; `switch`: the code was right, the switch failed.
   */
  | { step: 'error'; phase: 'send' | 'verify' | 'switch'; error: unknown; email: string | null };

type ClerkUser = NonNullable<ReturnType<typeof useUser>['user']>;
type ClerkEmail = ClerkUser['emailAddresses'][number];

const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export function useChangeEmail() {
  const { user } = useUser();
  const stepUp = useStepUp();
  const [state, setState] = useState<ChangeEmailState>({ step: 'idle' });

  const currentEmail = user?.primaryEmailAddress?.emailAddress ?? '';

  const fail = useCallback(
    (error: unknown, email: string | null, phase: 'send' | 'verify' | 'switch') => {
      if (email && phase === 'send' && classifyClerkError(error).kind === 'email_taken') {
        setState({ step: 'email_taken', email });
        return;
      }
      setState({ step: 'error', phase, error, email });
    },
    []
  );

  /** Remove the old primary once the new one has taken its place. */
  const removeOld = useCallback(
    async (email: string, old: ClerkEmail) => {
      const removed = await stepUp(() => old.destroy());
      await user?.reload();
      switch (removed.outcome) {
        case 'completed':
          setState({ step: 'done', email, linkedOld: null });
          return;
        case 'cancelled':
          setState({ step: 'old_kept', email, oldEmail: old.emailAddress, error: null });
          return;
        case 'failed':
          setState({ step: 'old_kept', email, oldEmail: old.emailAddress, error: removed.error });
          return;
        default: {
          const exhaustive: never = removed;
          throw new Error(`Unhandled step-up outcome: ${JSON.stringify(exhaustive)}`);
        }
      }
    },
    [stepUp, user]
  );

  /** Make the verified address the primary, then remove the old one. */
  const switchTo = useCallback(
    async (address: ClerkEmail) => {
      if (!user) return;
      const email = address.emailAddress;
      const old = user.primaryEmailAddress;
      const switched = await stepUp(() => user.update({ primaryEmailAddressId: address.id }));
      switch (switched.outcome) {
        case 'cancelled':
          setState({ step: 'code_sent', email });
          return;
        case 'failed':
          fail(switched.error, email, 'switch');
          return;
        case 'completed':
          break;
        default: {
          const exhaustive: never = switched;
          throw new Error(`Unhandled step-up outcome: ${JSON.stringify(exhaustive)}`);
        }
      }
      if (!old || old.id === address.id) {
        await user.reload();
        setState({ step: 'done', email, linkedOld: null });
        return;
      }
      const link = old.linkedTo[0];
      if (link) {
        const provider = socialProviderOf(link.type);
        await user.reload();
        setState({
          step: 'done',
          email,
          linkedOld: {
            email: old.emailAddress,
            provider: provider ? SOCIAL_PROVIDER_NAME[provider] : null,
          },
        });
        return;
      }
      await removeOld(email, old);
    },
    [user, stepUp, fail, removeOld]
  );

  /** Step 2: add the address (or reuse it) and have Clerk send a code to it. */
  const start = useCallback(
    async (rawEmail: string) => {
      if (!user) return;
      const email = rawEmail.trim().toLowerCase();
      setState({ step: 'sending', email });

      const existing = user.emailAddresses.find((e) => same(e.emailAddress, email));
      // Already proven on this account (an invitation's add-and-verify, say).
      if (existing?.verification?.status === 'verified') {
        await switchTo(existing);
        return;
      }

      let address = existing;
      if (!address) {
        const added = await stepUp(() => user.createEmailAddress({ email }));
        switch (added.outcome) {
          case 'cancelled':
            setState({ step: 'editing' });
            return;
          case 'failed':
            fail(added.error, email, 'send');
            return;
          case 'completed':
            address = added.value;
            break;
          default: {
            const exhaustive: never = added;
            throw new Error(`Unhandled step-up outcome: ${JSON.stringify(exhaustive)}`);
          }
        }
      }
      try {
        await address.prepareVerification({ strategy: 'email_code' });
        setState({ step: 'code_sent', email });
      } catch (err: unknown) {
        fail(err, email, 'send');
      }
    },
    [user, stepUp, switchTo, fail]
  );

  /** Step 3: the code proves the new address; then it becomes the sign-in email. */
  const verify = useCallback(
    async (email: string, code: string) => {
      if (!user) return;
      setState({ step: 'verifying', email });
      const address = user.emailAddresses.find((e) => same(e.emailAddress, email));
      if (!address) {
        fail(new Error('The new address is not on the Clerk user'), email, 'verify');
        return;
      }
      // Proven already — a switch that failed after the code was right. Another
      // attempt at the code would be refused; the switch is what is left.
      if (address.verification?.status === 'verified') {
        await switchTo(address);
        return;
      }
      try {
        const verified = await address.attemptVerification({ code });
        await switchTo(verified);
      } catch (err: unknown) {
        fail(err, email, 'verify');
      }
    },
    [user, switchTo, fail]
  );

  /** A new code to the same address. */
  const resend = useCallback(
    async (email: string) => {
      const address = user?.emailAddresses.find((e) => same(e.emailAddress, email));
      if (!address) return;
      try {
        await address.prepareVerification({ strategy: 'email_code' });
        setState({ step: 'code_sent', email });
      } catch (err: unknown) {
        fail(err, email, 'send');
      }
    },
    [user, fail]
  );

  /** Try again to remove an old address that stayed. */
  const retryRemoveOld = useCallback(
    async (email: string, oldEmail: string) => {
      const old = user?.emailAddresses.find((e) => same(e.emailAddress, oldEmail));
      if (!old) {
        setState({ step: 'done', email, linkedOld: null });
        return;
      }
      await removeOld(email, old);
    },
    [user, removeOld]
  );

  return {
    state,
    currentEmail,
    open: useCallback(() => setState({ step: 'editing' }), []),
    close: useCallback(() => setState({ step: 'idle' }), []),
    start,
    verify,
    resend,
    retryRemoveOld,
  };
}
